import { FunctionComponent, useEffect, useState } from 'react'
import { OccasionalSlotsPlaces, RegularCancellationRequest, RegularSlot, RequestSlot } from '@/types/Slots'
import { useLazyQuery } from '@apollo/client'
import { GET_CHILD_LIST } from '@/modules/API/parent/parentHandler'
import { GET_SLOT_REQUEST } from '@/modules/API/slot/requestSlotsHandler'
import { GET_REGULAR_SLOTS } from '@/modules/API/slot/regularSlotHandler'
import Indicator from '@/components/_custom/Indicator/indicator'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector'
import moment from 'moment/moment'
import styles from './request.module.scss'
import { useTranslation } from 'react-i18next'
import RequestTable from './requestTable/requestTable'
import Title from '@/components/_custom/Title/title'
import { Navigate, NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import { getTextFromLanguage } from '@/modules/helper/languageHelper'
import { useAuth } from '@/modules/context/AuthContext'
import { ArrowBack } from '@mui/icons-material'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Loading from '@/components/Loading/Loading'
import { ChildWithParents } from '@/types/Child'
import { GET_PLACES } from '@/modules/API/daycare/daycareHandler.ts'

enum RequestType {
    UPDATE = 'update',
    ABSENCE = 'absence',
    REQUEST = 'request'
}

const Request: FunctionComponent = () => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const params: URLSearchParams = new URLSearchParams(useLocation().search)

    const tabValue: string | null = params.get('tab')

    const [typeParams, setTypeParams] = useState<RequestType[]>([
        RequestType.UPDATE,
        RequestType.ABSENCE,
        RequestType.REQUEST
    ])

    const [openFamily, setOpenFamily] = useState<boolean>(false)

    useEffect(() => {
        const type: string | null = params.get('type')
        switch (type) {
            case 'regular':
                setTypeParams([RequestType.ABSENCE])
                setOpenFamily(true)
                break
            case 'slot':
                setTypeParams([RequestType.REQUEST])
                setOpenFamily(true)
                break
            default:
                setTypeParams([RequestType.UPDATE, RequestType.ABSENCE, RequestType.REQUEST])
                break
        }
    }, [params.get('type')])

    const [updateRequest, setUpdateRequest] = useState<RegularCancellationRequest[]>([])
    const [absenceRequest, setAbsenceRequest] = useState<RegularCancellationRequest[]>([])

    const [getRegularSlots, { loading: loadingRegularSlots, data: dataRegularSlots }] = useLazyQuery(
        GET_REGULAR_SLOTS,
        {
            variables: {
                daycareId: user?.currentDaycare?.id
            }
        }
    )

    useEffect(() => {
        if (dataRegularSlots) {
            const newUpdateRequests: RegularCancellationRequest[] = []
            const newAbsenceRequests: RegularCancellationRequest[] = []

            dataRegularSlots.getRegularSlots.forEach((slot: RegularSlot) => {
                slot.cancellationRequests.forEach((request: RegularCancellationRequest) => {
                    if (moment(request.date, 'DD-MM-YYYY').isSameOrAfter(moment(), 'day')) {
                        const newUpdate: RegularCancellationRequest = {
                            ...request,
                            childId: slot.childId,
                            childName: slot.childName,
                            childComplement: slot.childComplement,
                            parentName: slot.parentName,
                            parentId: slot.parentId,
                            regularSlotStart: slot.start,
                            regularSlotEnd: slot.end,
                            regularSlotId: slot.id,
                            placeId: slot.placeId,
                            placeName: slot.placeName,
                            placeAddress: slot.placeAddress
                        }

                        if (
                            moment(slot.start, 'HH:mm:ss').format('HH:mm') ===
                                moment(newUpdate.start, 'HH:mm:ss').format('HH:mm') &&
                            moment(slot.end, 'HH:mm:ss').format('HH:mm') ===
                                moment(newUpdate.end, 'HH:mm:ss').format('HH:mm')
                        ) {
                            newAbsenceRequests.push(newUpdate)
                            if (typeParams.includes(RequestType.ABSENCE)) {
                                setRequestList((prev: (RegularCancellationRequest | RequestSlot)[]) => [
                                    ...prev,
                                    newUpdate
                                ])
                            }
                        } else {
                            newUpdateRequests.push(newUpdate)
                            if (typeParams.includes(RequestType.UPDATE)) {
                                setRequestList((prev: (RegularCancellationRequest | RequestSlot)[]) => [
                                    ...prev,
                                    newUpdate
                                ])
                            }
                        }
                    }
                })
            })

            setUpdateRequest(newUpdateRequests)
            setAbsenceRequest(newAbsenceRequests)
        }
    }, [dataRegularSlots])

    const [requestSlots, setRequestSlots] = useState<RequestSlot[]>([])

    const [getRequestSlots, { loading: loadingRequestSlots, data: dataRequestSlots }] = useLazyQuery(GET_SLOT_REQUEST, {
        variables: {
            daycare_id: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataRequestSlots) {
            setRequestSlots(dataRequestSlots.getRequestSlots)
            if (typeParams.includes(RequestType.REQUEST)) {
                setRequestList((prev: (RegularCancellationRequest | RequestSlot)[]) => [
                    ...prev,
                    ...dataRequestSlots.getRequestSlots
                ])
            }
        }
    }, [dataRequestSlots])

    const [childs, setChilds] = useState<ChildWithParents[]>([])

    const [getChildList, { loading: loadingChilds, data: dataChilds }] = useLazyQuery(GET_CHILD_LIST, {
        variables: {
            daycareId: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataChilds) {
            setChilds(dataChilds.getChildList)
        }
    }, [dataChilds])

    const [requestList, setRequestList] = useState<(RegularCancellationRequest | RequestSlot)[]>([])

    function selectAbsenceRequest(): void {
        setRequestList(absenceRequest)
    }

    function selectUpdateRequest(): void {
        setRequestList(updateRequest)
    }

    function selectRequestSlot(): void {
        setRequestList(requestSlots)
    }

    function selectAllRequest(): void {
        setRequestList([...updateRequest, ...absenceRequest, ...requestSlots])
    }

    const [places, setPlaces] = useState<OccasionalSlotsPlaces[]>([])

    const [getDaycarePlaces, { data: dataPlaces }] = useLazyQuery(GET_PLACES, {
        variables: {
            daycareId: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataPlaces) setPlaces(dataPlaces.getOccasionalSlotsPlaces)
    }, [dataPlaces])

    useEffect(() => {
        refetchData()
    }, [user?.currentDaycare?.id])

    async function refetchData(): Promise<void> {
        setRequestList([])
        setUpdateRequest([])
        setAbsenceRequest([])
        setRequestSlots([])

        await Promise.all([getRegularSlots(), getRequestSlots(), getChildList(), getDaycarePlaces()])
    }

    useEffect(() => {
        moment.locale(getTextFromLanguage())
    }, [])

    const canReadOccasionalSlot: boolean = !!user?.currentDaycareRights.canReadOccasionalSlot
    const canWriteOccasionalSlot: boolean = !!user?.currentDaycareRights.canWriteOccasionalSlot
    const canReadContracts: boolean = !!user?.currentDaycareRights.canReadContracts
    const canWriteContracts: boolean = !!user?.currentDaycareRights.canWriteContracts

    return user && user.currentDaycare && (canReadContracts || canReadOccasionalSlot) ? (
        <>
            {(loadingChilds || loadingRegularSlots || loadingRequestSlots) && <Loading overall={true} />}

            <div className={styles.PagePosition}>
                {user.daycares.length > 1 && (
                    <div className={styles.HeaderRow}>
                        <div className={styles.DaycareSelector}>
                            <DaycareSelector />
                        </div>
                    </div>
                )}
                <div className={styles.Container}>
                    <div className={styles.TitleContainer}>
                        <Title text={t('myWaitingRequests')} className={styles.Title} />
                    </div>
                    <div className={styles.IndicatorsContainer}>
                        <a className={styles.Clickable} onClick={selectAllRequest}>
                            <Indicator
                                count={[...updateRequest, ...absenceRequest, ...requestSlots].length}
                                text={t('total')}
                            />
                        </a>
                        {canReadContracts && (
                            <>
                                <a className={styles.Clickable} onClick={selectUpdateRequest}>
                                    <Indicator
                                        count={updateRequest.length}
                                        text={updateRequest.length > 1 ? t('modifications') : t('modification')}
                                        badge={'regularRequest'}
                                    />
                                </a>
                                <a className={styles.Clickable} onClick={selectAbsenceRequest}>
                                    <Indicator
                                        count={absenceRequest.length}
                                        text={absenceRequest.length > 1 ? t('absences') : t('absence')}
                                        badge={'absence'}
                                    />
                                </a>
                            </>
                        )}
                        {canReadOccasionalSlot && (
                            <a className={styles.Clickable} onClick={selectRequestSlot}>
                                <Indicator
                                    count={requestSlots.length}
                                    text={requestSlots.length > 1 ? t('slots') : t('slot')}
                                    badge={'request'}
                                />
                            </a>
                        )}
                    </div>
                    <div className={styles.TableContainer}>
                        <RequestTable
                            requestList={requestList}
                            currentDaycare={user.currentDaycare}
                            refetchData={refetchData}
                            regularCancellationRequest={[...updateRequest, ...absenceRequest]}
                            openFamily={openFamily}
                            childs={childs}
                            places={places}
                            canWriteContracts={canWriteContracts}
                            canWriteOccasionalSlot={canWriteOccasionalSlot}
                        />
                    </div>
                </div>
                <ButtonTextIcon
                    type={'custom'}
                    customText={t('backToPlanning')}
                    customIcon={<ArrowBack />}
                    onClick={() => navigate(tabValue ? `/calendar?tab=${tabValue}` : '/calendar')}
                />
            </div>
        </>
    ) : (
        <Navigate to='/' replace />
    )
}

export default Request
